import { useCallback, useEffect, useRef, useState } from "react";
import {
  createTimeSlicedSearchIndex,
  isAbortError,
  type RankedSearchConfig,
  type TimeSlicedSearchIndex,
} from "@stockline/product-search";

/**
 * Skip the fuzzy (typo-tolerance) tier for queries shorter than this. On 1-2
 * characters the fuzzy pass is the most expensive part of the search and only
 * yields noise (threshold-0.4 matches on two letters); prefix/substring tiers
 * already cover what the user expects. Fuzzy kicks in from 3 chars — where
 * typos become plausible and match sets shrink (STK-1399).
 */
const DEFAULT_FUZZY_MIN_QUERY_LENGTH = 3;

export interface TimeSlicedSearchResults<T> {
  /** The trimmed term these items answer — compare before displaying. */
  term: string;
  items: T[];
}

interface UseTimeSlicedSearchOptions<T> {
  /**
   * Corpus to search. The underlying index is memoized on this array's
   * IDENTITY — pass a stable (memoized) array and the chunk sub-indexes
   * build once per catalog, not per render.
   */
  corpus: T[];
  searchConfig: RankedSearchConfig<T>;
  /**
   * Gate, typically "dropdown is open". While false the index stays cold and
   * `results` resets to null; on the false→true edge the index warms up
   * eagerly (time-sliced, so it never blocks typing).
   */
  enabled: boolean;
  /**
   * Settled (debounced + trimmed) query to answer. Pass "" to idle — the
   * consumer's synchronous regimes (empty box, untouched seed, SKU prefix
   * path) don't need the async search.
   */
  term: string;
  /** See {@link DEFAULT_FUZZY_MIN_QUERY_LENGTH}. */
  fuzzyMinQueryLength?: number;
}

/**
 * Shared async search wiring for large-catalog picker dropdowns (STK-1399):
 * the sender/recipient pickers (`EntitySelectWithTags`) and the recap product
 * combo (`useProductSelectData`) both run their typed-query search through
 * this hook.
 *
 * A full `rankedFuzzyFilter` over a large catalog (35k+ rows) is a single
 * ~85-175ms synchronous call — run on a keystroke it blocks the main thread
 * and typed characters echo late. This hook owns the fix:
 *   - a `createTimeSlicedSearchIndex` memoized on the corpus identity
 *     (chunked scan, ~6-10ms main-thread slices with yields between),
 *   - eager warm-up while `enabled` so the first keystroke pays no build,
 *   - one in-flight search per settled term, aborted (`AbortController`)
 *     whenever the term changes — a new keystroke cancels stale work at the
 *     next chunk boundary.
 *
 * Results carry the term they answered so consumers never display a stale
 * reply as current: render `results.items` only when `results.term` matches
 * the current term, otherwise keep the previous list on screen and show a
 * pending indicator.
 */
export function useTimeSlicedSearch<T>({
  corpus,
  searchConfig,
  enabled,
  term,
  fuzzyMinQueryLength = DEFAULT_FUZZY_MIN_QUERY_LENGTH,
}: UseTimeSlicedSearchOptions<T>): {
  results: TimeSlicedSearchResults<T> | null;
} {
  const indexRef = useRef<{
    corpus: T[];
    searchConfig: RankedSearchConfig<T>;
    fuzzyMinQueryLength: number;
    index: TimeSlicedSearchIndex<T>;
  } | null>(null);
  const getIndex = useCallback(() => {
    if (!corpus.length) return null;
    // Rebuild when any index-defining input changes identity, not corpus
    // alone — a searchConfig/fuzzyMinQueryLength change with a stable corpus
    // must not silently reuse an index built with the old options.
    let entry = indexRef.current;
    if (
      entry?.corpus !== corpus ||
      entry?.searchConfig !== searchConfig ||
      entry?.fuzzyMinQueryLength !== fuzzyMinQueryLength
    ) {
      entry = {
        corpus,
        searchConfig,
        fuzzyMinQueryLength,
        index: createTimeSlicedSearchIndex(corpus, searchConfig, {
          fuzzyMinQueryLength,
        }),
      };
      indexRef.current = entry;
    }
    return entry.index;
  }, [corpus, searchConfig, fuzzyMinQueryLength]);

  // Eager warm-up: build the chunk sub-indexes ahead of the first keystroke.
  // The build itself is time-sliced (one chunk per main-thread slice), so
  // typing during warm-up stays responsive and `search` picks up any chunks
  // still missing on demand.
  useEffect(() => {
    if (!enabled) return;
    const index = getIndex();
    if (!index) return;
    const controller = new AbortController();
    index.warmUp({ signal: controller.signal }).catch((err) => {
      if (!isAbortError(err)) {
        console.error("useTimeSlicedSearch: index warm-up failed", err);
      }
    });
    return () => controller.abort();
  }, [enabled, getIndex]);

  const [results, setResults] = useState<TimeSlicedSearchResults<T> | null>(
    null,
  );

  // One in-flight search per settled term; the cleanup aborts it whenever the
  // term changes or the consumer disables the search.
  useEffect(() => {
    if (!enabled) {
      setResults(null);
      return;
    }
    if (!term) return;
    const index = getIndex();
    if (!index) return;
    const controller = new AbortController();
    index.search(term, { signal: controller.signal }).then(
      (searchResults) =>
        setResults({ term, items: searchResults.map((r) => r.item) }),
      (err) => {
        if (!isAbortError(err)) {
          console.error("useTimeSlicedSearch: search failed", err);
        }
      },
    );
    return () => controller.abort();
  }, [enabled, term, getIndex]);

  return { results };
}
